import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../context/AppContext";
import AdminNavbar from "../components/AdminNavbar";
import toast from "react-hot-toast";

const STATUS_COLORS = {
  pending: "#FFB347",
  confirmed: "#2196F3",
  preparing: "#9C27B0",
  shipped: "#FF6B35",
  out_for_delivery: "#FF9800",
  delivered: "#2D8B4E",
  cancelled: "#f44336",
};

const STAT_CARDS = [
  {
    key: "totalOrders",
    label: "Total Orders",
    gradient: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
    emoji: "🛒",
  },
  {
    key: "todayOrders",
    label: "Today's Orders",
    gradient: "linear-gradient(135deg,#1565c0,#1976d2)",
    emoji: "📅",
  },
  {
    key: "totalUsers",
    label: "Total Users",
    gradient: "linear-gradient(135deg,#7b1fa2,#9c27b0)",
    emoji: "👥",
  },
  {
    key: "totalRevenue",
    label: "Revenue",
    gradient: "linear-gradient(135deg,#e65100,#ff9800)",
    emoji: "💰",
    prefix: "₹",
  },
  {
    key: "totalProducts",
    label: "Products",
    gradient: "linear-gradient(135deg,#00838f,#00acc1)",
    emoji: "🥤",
  },
  {
    key: "lowStockCount",
    label: "Low Stock Alert",
    gradient: "linear-gradient(135deg,#c62828,#f44336)",
    emoji: "⚠️",
  },
];

const QUICK_ACTIONS = [
  { label: "Manage Orders", path: "/admin/orders", color: "#2D8B4E" },
  { label: "Add Product", path: "/admin/products/add", color: "#1976d2" },
  { label: "Manage Users", path: "/admin/users", color: "#9c27b0" },
  { label: "Inventory", path: "/admin/inventory", color: "#ff9800" },
  { label: "Manage Categories", path: "/admin/categories", color: "#0891b2" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queries, setQueries] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [qLoading, setQLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(null);

  useEffect(() => {
    api
      .get("/admin/dashboard")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load dashboard"))
      .finally(() => setLoading(false));
  }, []);

  const loadQueries = () => {
    setQLoading(true);
    api
      .get("/contact/queries?limit=10")
      .then((r) => {
        setQueries(r.data.queries || []);
        setNewCount(r.data.unreadCount || 0);
      })
      .catch(() => {})
      .finally(() => setQLoading(false));
  };

  useEffect(() => {
    loadQueries();
  }, []);

  const updateQueryStatus = async (id, status) => {
    setStatusUpdating(id);
    try {
      await api.patch(`/contact/queries/${id}`, { status });
      setQueries((prev) =>
        prev.map((q) => (q._id === id ? { ...q, status } : q)),
      );
      if (status !== "new") setNewCount((n) => Math.max(0, n - 1));
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update");
    } finally {
      setStatusUpdating(null);
    }
  };

  const deleteQuery = async (id) => {
    try {
      await api.delete(`/contact/queries/${id}`);
      setQueries((prev) => prev.filter((q) => q._id !== id));
      toast.success("Query deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        })
      : "—";

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg,#f0f9f0,#e8f5e9)" }}
    >
      <AdminNavbar />
      <div className="max-w-screen-xl mx-auto p-4 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-black text-green-700">
            Admin Dashboard
          </h1>
          <p className="text-gray-400">
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-14 h-14 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 mb-8">
              {STAT_CARDS.map(
                ({ key, label, gradient, emoji, prefix = "" }) => (
                  <div
                    key={key}
                    className="glass-card p-5 hover:-translate-y-1 transition-all"
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-xl mb-3"
                      style={{ background: gradient }}
                    >
                      {emoji}
                    </div>
                    <p className="text-3xl font-black leading-none mb-1">
                      {prefix}
                      {(data?.stats?.[key] ?? 0).toLocaleString("en-IN")}
                    </p>
                    <p className="text-xs text-gray-500 font-semibold">
                      {label}
                    </p>
                  </div>
                ),
              )}
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Recent orders */}
              <div className="lg:col-span-2 admin-table-wrap overflow-hidden">
                <div
                  className="p-4 flex justify-between items-center"
                  style={{ borderBottom: "1px solid rgba(45,139,78,0.1)" }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🛒</span>
                    <h3 className="font-extrabold" style={{ color: "#0f5230" }}>
                      Recent Orders
                    </h3>
                  </div>
                  <button
                    onClick={() => navigate("/admin/orders")}
                    className="text-xs font-bold px-3 py-1.5 rounded-full transition-all hover:scale-105"
                    style={{
                      background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                      color: "white",
                      boxShadow: "0 2px 8px rgba(45,139,78,0.3)",
                    }}
                  >
                    View All →
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="admin-table">
                    <thead className="admin-thead">
                      <tr>
                        {[
                          "Order ID",
                          "Customer",
                          "Amount",
                          "Status",
                          "Date",
                        ].map((h) => (
                          <th key={h} className="admin-th">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.recentOrders || []).map((o, idx) => (
                        <tr
                          key={o._id}
                          className="admin-tr cursor-pointer"
                          style={{ animationDelay: `${idx * 0.05}s` }}
                          onClick={() => navigate("/admin/orders")}
                        >
                          <td className="admin-td">
                            <span className="font-black text-green-600">
                              #{o.orderId}
                            </span>
                          </td>
                          <td className="admin-td font-semibold">
                            {o.customerDetails?.name || o.user?.name || "—"}
                          </td>
                          <td className="admin-td">
                            <span className="font-black text-green-600 text-base">
                              ₹{o.total}
                            </span>
                          </td>
                          <td className="admin-td">
                            <span
                              className="status-chip text-white font-bold capitalize"
                              style={{
                                background: STATUS_COLORS[o.orderStatus],
                                boxShadow: `0 2px 8px ${STATUS_COLORS[o.orderStatus]}50`,
                              }}
                            >
                              {o.orderStatus?.replace(/_/g, " ")}
                            </span>
                          </td>
                          <td className="admin-td text-xs text-gray-400">
                            {fmtDate(o.createdAt)}
                          </td>
                        </tr>
                      ))}
                      {!data?.recentOrders?.length && (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center py-10 text-gray-400"
                          >
                            <div className="text-3xl mb-2">📭</div>
                            <p className="font-semibold">No orders yet</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Right column */}
              <div className="flex flex-col gap-5">
                {/* Status breakdown */}
                <div className="glass-card p-4">
                  <h3 className="font-extrabold text-green-700 mb-4">
                    Orders by Status
                  </h3>
                  <div className="flex flex-col gap-2">
                    {(data?.ordersByStatus || []).map(({ _id, count }) => (
                      <div
                        key={_id}
                        className="flex justify-between items-center"
                      >
                        <span
                          className="chip text-xs font-bold capitalize"
                          style={{
                            background: `${STATUS_COLORS[_id] ?? "#999"}18`,
                            color: STATUS_COLORS[_id] ?? "#999",
                            border: `1px solid ${STATUS_COLORS[_id] ?? "#999"}30`,
                          }}
                        >
                          {_id?.replace(/_/g, " ")}
                        </span>
                        <span
                          className="font-extrabold"
                          style={{ color: STATUS_COLORS[_id] ?? "#999" }}
                        >
                          {count}
                        </span>
                      </div>
                    ))}
                    {!data?.ordersByStatus?.length && (
                      <p className="text-gray-400 text-sm">No data</p>
                    )}
                  </div>
                </div>

                {/* Quick actions */}
                <div className="glass-card p-4">
                  <h3 className="font-extrabold text-green-700 mb-4">
                    Quick Actions
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {QUICK_ACTIONS.map(({ label, path, color }) => (
                      <button
                        key={path}
                        onClick={() => navigate(path)}
                        className="rounded-2xl py-3 border-2 text-xs font-bold transition-all hover:-translate-y-0.5"
                        style={{
                          borderColor: `${color}35`,
                          color,
                          background: `${color}08`,
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Contact Queries ── */}
            <div className="glass-card p-5 mt-5">
              <div className="flex justify-between items-center mb-5 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <h3
                    className="font-extrabold text-lg"
                    style={{ color: "#0f5230" }}
                  >
                    Contact Queries
                  </h3>
                  {newCount > 0 && (
                    <span
                      className="px-2.5 py-0.5 rounded-full text-xs font-extrabold text-white"
                      style={{
                        background: "linear-gradient(135deg,#ef4444,#dc2626)",
                      }}
                    >
                      {newCount} new
                    </span>
                  )}
                </div>
                <button
                  onClick={() => navigate("/admin/contact")}
                  className="text-xs font-bold px-3 py-1.5 rounded-full transition-all hover:scale-105"
                  style={{
                    background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                    color: "white",
                    boxShadow: "0 2px 8px rgba(45,139,78,0.3)",
                  }}
                >
                  View All →
                </button>
              </div>

              {qLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-green-300 border-t-green-600 rounded-full animate-spin" />
                </div>
              ) : queries.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <div className="text-3xl mb-2">📭</div>
                  <p className="font-semibold text-sm">
                    No contact queries yet
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="admin-table">
                    <thead className="admin-thead">
                      <tr>
                        {[
                          "Name",
                          "Contact",
                          "Subject",
                          "Message",
                          "Status",
                          "Actions",
                        ].map((h) => (
                          <th key={h} className="admin-th">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {queries.map((q, idx) => (
                        <tr
                          key={q._id}
                          className="admin-tr"
                          style={{ animationDelay: `${idx * 0.04}s` }}
                        >
                          <td className="admin-td">
                            <p className="font-bold text-sm leading-tight">
                              {q.name}
                            </p>
                            <p className="text-xs text-gray-400">
                              {fmtDate(q.createdAt)}
                            </p>
                          </td>
                          <td className="admin-td">
                            {q.email && (
                              <p className="text-xs font-semibold">{q.email}</p>
                            )}
                            {q.phone && (
                              <p className="text-xs text-gray-400">{q.phone}</p>
                            )}
                          </td>
                          <td className="admin-td">
                            <span className="status-chip bg-blue-50 text-blue-700 border border-blue-100 text-xs">
                              {q.subject}
                            </span>
                          </td>
                          <td className="admin-td" style={{ maxWidth: 220 }}>
                            <p
                              className="text-xs text-gray-600 leading-relaxed"
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {q.message}
                            </p>
                          </td>
                          <td className="admin-td">
                            <select
                              value={q.status}
                              disabled={statusUpdating === q._id}
                              onChange={(e) =>
                                updateQueryStatus(q._id, e.target.value)
                              }
                              className="text-xs font-bold rounded-lg px-2 py-1 border cursor-pointer outline-none"
                              style={{
                                background:
                                  q.status === "unread"
                                    ? "#fef3f2"
                                    : q.status === "read"
                                      ? "#fffbeb"
                                      : "#f0fdf4",
                                color:
                                  q.status === "unread"
                                    ? "#ef4444"
                                    : q.status === "read"
                                      ? "#d97706"
                                      : "#16a34a",
                                borderColor:
                                  q.status === "unread"
                                    ? "#fca5a5"
                                    : q.status === "read"
                                      ? "#fde68a"
                                      : "#bbf7d0",
                              }}
                            >
                              <option value="unread">New</option>
                              <option value="read">Read</option>
                              <option value="resolved">Resolved</option>
                            </select>
                          </td>
                          <td className="admin-td">
                            <button
                              onClick={() => deleteQuery(q._id)}
                              className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-colors text-sm"
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
